import { TranslationKey } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import Zxcvbn from 'zxcvbn';
import { InputStatus } from '../../../models/error.model';
import { PasswordWithScoreInputs } from '../components/PasswordWithScoreInputs';

const ERROR_CODES: Record<string, TranslationKey> = {
  PASSWORD_EMPTY: 'field_cant_be_empty',
  PASSWORD_ANY_NON_WHITE_CHAR: 'password_any_nonwhite_char',
  PASSWORD_TOO_WEAK: 'password_too_weak',
  REPEAT_PASSWORD_EMPTY: 'field_cant_be_empty',
  REPEAT_PASSWORD_DONT_MATCH: 'repeat_password_dont_match',
};

const MIN_PASSWORD_SCORE: number = 2;

interface IProps {
  onPasswordChange(password: string, score: number): void;
  onPasswordStatusChange(passwordStatus: InputStatus): void;
  labelText: string;
  autoComplete?: string;
}

export const PasswordWithScoreInputsContainer = observer((props: IProps): ReactElement => {
  const [passwordScore, setPasswordScore] = useState<number | null>(null);
  const [passwordStatus, setPasswordStatus] = useState<InputStatus>(InputStatus.empty());

  const handlePasswordChange = (value: string): void => {
    const score = value ? Zxcvbn(value).score : null;
    setPasswordScore(score);
    props.onPasswordChange(value, score ?? 0);

    setPasswordStatus(InputStatus.empty());
    props.onPasswordStatusChange(InputStatus.empty());
  };

  const validatePassword = (value: string, score: number | null): InputStatus => {
    const errorCodes: TranslationKey[] = [];

    if (!value) {
      errorCodes.push(ERROR_CODES.PASSWORD_EMPTY);
    }

    if (value && value.trim() === '') {
      errorCodes.push(ERROR_CODES.PASSWORD_ANY_NON_WHITE_CHAR);
    }

    if (score !== null && score < MIN_PASSWORD_SCORE) {
      errorCodes.push(ERROR_CODES.PASSWORD_TOO_WEAK);
    }

    return InputStatus.buildFrom(errorCodes);
  };

  const handleBlur = (value: string): void => {
    const score = value ? Zxcvbn(value).score : null;
    const status = validatePassword(value, score);
    setPasswordScore(score);
    setPasswordStatus(status);
    props.onPasswordChange(value, score ?? 0);
    props.onPasswordStatusChange(status);
  };

  return (
    <PasswordWithScoreInputs
      onPasswordChange={handlePasswordChange}
      onBlur={handleBlur}
      passwordStatus={passwordStatus}
      passwordScore={passwordScore}
      labelText={props.labelText}
      autoComplete={props.autoComplete}
    />
  );
});
